import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {jwtDecode} from 'jwt-decode';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Auth from './components/Auth';
import Datasets from './components/Datasets';
import AIModels from './components/AIModels';
import Projects from './components/Projects';
import Communications from './components/Communications';
import Organization from './components/Organization';
import TopBar from './components/TopBar';
import Settings from './components/Settings';
import RequestPasswordReset from './components/RequestPasswordReset';
import ResetPassword from './components/ResetPassword';
import './App.css';
import FAQ from './components/FAQ';

const App = () => {
  const [isAuth, setIsAuth] = useState(() => localStorage.getItem('accessToken') ? true : false);
  const [isLoading, setIsLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [isMaintenance, setIsMaintenance] = useState(false);  // Maintenance mode state
  const location = useLocation();
  const navigate = useNavigate();

  // Axios interceptor to catch 503 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 503) {
          setIsMaintenance(true);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const response = await axios.get('/api/check_maintenance');
        if (response.data.maintenanceMode) {
          setIsMaintenance(true);
        }
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
      }
    };

    const checkToken = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          const currentTime = Date.now() / 1000;

          if (decodedToken.exp < currentTime) {
            setIsAuth(false);
            localStorage.removeItem('accessToken');
          } else {
            setIsAuth(true);
            const timeUntilExpiry = decodedToken.exp * 1000 - Date.now();
            setTimeout(refreshToken, timeUntilExpiry - 60000);
            
            if (location.pathname === '/') {
              navigate('/dashboard');
            }
          }
        } catch (error) {
          console.error('Invalid token:', error);
          setIsAuth(false);
          localStorage.removeItem('accessToken');
        }
      } else {
        setIsAuth(false);
      }
      setIsLoading(false);
    };

    checkMaintenanceMode();  // Check for maintenance mode first
    checkToken();  // Then check for the token
  }, [location.pathname, navigate]);

  const refreshToken = async () => {
    try {
      const response = await axios.post('/api/refresh-token', {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      localStorage.setItem('accessToken', response.data.accessToken);
      setIsAuth(true);
    } catch (error) {
      console.error('Error refreshing token:', error);
      setIsAuth(false);
      localStorage.removeItem('accessToken');
      navigate('/login');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    setIsAuth(false);
    navigate('/login');
  };

  const handleLogin = () => {
    setIsAuth(true);
    navigate('/dashboard');
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // If the site is in maintenance mode, show the maintenance page
  if (isMaintenance) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>The site is currently under maintenance.</h1>
        <p>Please try again later.</p>
      </div>
    );
  }

  // Public routes that don't require authentication
  const publicRoutes = ['/login', '/request_password_reset', '/reset_password'];

  // Check if the current route is a public route
  const isPublicRoute = publicRoutes.some(route => 
    location.pathname.startsWith(route)
  );

  return (
    <div style={{ display: 'flex' }}>
      {isAuth && <Sidebar handleLogout={handleLogout} />}
      <main style={{ flexGrow: 1, padding: '16px' }}>
        {isAuth && <TopBar darkMode={darkMode} setDarkMode={setDarkMode} handleLogout={handleLogout} />}
        <Routes>
          <Route path="/login" element={<Auth setAuth={handleLogin} />} />
          <Route path="/request_password_reset" element={<RequestPasswordReset setDarkMode={setDarkMode} darkMode={darkMode} />} />
          <Route path="/reset_password/:token" element={<ResetPassword />} />
          
          <Route path="/" element={
            isAuth ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } />
          <Route path="/dashboard" element={
            isAuth ? (
              <Dashboard setAuth={setIsAuth} setDarkMode={setDarkMode} darkMode={darkMode} handleLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="/datasets" element={
            isAuth ? (
              <Datasets setDarkMode={setDarkMode} darkMode={darkMode} handleLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="/ai-models" element={
            isAuth ? (
              <AIModels setDarkMode={setDarkMode} darkMode={darkMode} handleLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="/projects" element={
            isAuth ? (
              <Projects setDarkMode={setDarkMode} darkMode={darkMode} handleLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="/communications" element={
            isAuth ? (
              <Communications setDarkMode={setDarkMode} darkMode={darkMode} handleLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="/organization" element={
            isAuth ? (
              <Organization setDarkMode={setDarkMode} darkMode={darkMode} handleLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="/faq" element={
            isAuth ? (
              <FAQ setDarkMode={setDarkMode} darkMode={darkMode} handleLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="/settings" element={
            isAuth ? (
              <Settings setDarkMode={setDarkMode} darkMode={darkMode} handleLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="*" element={<Navigate to={isAuth ? "/dashboard" : "/login"} replace />} />
        </Routes>
      </main>
    </div>
  );
};

const AppWrapper = () => (
  <Router>
    <App />
  </Router>
);

export default AppWrapper;
